import { CONVERSATION_ACTIVITY_EVENTS } from '../../../services/messaging-internal-events';
import { EXTERNAL_SCOPE_TYPES } from '../../../models/messaging-user';
import { MODEL_NAMES } from '../../../constants/index';
import { conversationHasExactContacts } from '../../../utils/messaging/conversation';
import { filterSystemAndCurrentUser, matchingUsers } from '../../../utils/messaging/user';
import { service } from '@ember/service';
import { setOwner } from '@ember/application';

export default class WebsocketActivityHandlersConversation {
  @service messaging;
  @service messagingInternalEvents;
  @service messagingNavigation;
  @service store;

  constructor(owner) {
    setOwner(this, owner);
  }

  handleActivity(response) {
    const statusHandlers = {
      updated: payload => this.#handleUpdatedActivity(payload),
      left: payload => this.#handleUpdatedActivity(payload, false),
      read: payload => this.#handleReadActivity(payload),
      replace: payload =>
        this.messagingInternalEvents.emit(CONVERSATION_ACTIVITY_EVENTS.REPLACE, payload),
      destroyed: payload =>
        this.messagingInternalEvents.emit(CONVERSATION_ACTIVITY_EVENTS.DESTROYED, payload),
    };
    const handler = response.status ? statusHandlers[response.status] : statusHandlers.updated;
    handler(response.payload);
  }

  #handleUpdatedActivity(payload, showToast = true) {
    if (this.#isSavingMatchingDraft(payload)) {
      return;
    }

    // Flag off: do not mutate the in-memory list or store. Refresh still hides
    // inaccessible threads via the index payload.
    if (!this.messaging.groupMessagingEnabled && this.#payloadDisabled(payload)) {
      return;
    }

    const record = this.#ensureConversationInStore(payload.data.id, payload);

    if (record.disabled) {
      if (this.messaging.groupMessagingEnabled) {
        const eventPayload = {
          conversationId: record.id,
        };
        if (!showToast) {
          eventPayload.showToast = false;
        }
        this.messagingInternalEvents.emit(
          CONVERSATION_ACTIVITY_EVENTS.ACCESS_REVOKED,
          eventPayload
        );
      }
      return;
    }

    if (!this.#conversationInList(this.messaging.conversations, record.id)) {
      this.#addOrReplaceConversationRecord(this.messaging.conversations, record);
    }

    const currentHandler = this.messagingNavigation.currentConversationHandler;
    if (currentHandler?.conversation?.id === record.id && !record.inactive) {
      currentHandler.resetInactiveReasons();
    }
  }

  // Guards against an ID-collision race: while a virtual (unsaved) conversation
  // is mid-save, the server may broadcast `conversation.created`/`updated` for
  // that same conversation. If we let the broadcast through it would call
  // `store.pushPayload({ data: { id: '<new id>' } })` before `save()` resolves,
  // creating a second Ember Data record with the same id and leaving the list
  // pointing at a stale ghost.
  //
  // We identify the "same" conversation by matching the payload's member roster
  // against the saving conversation's `intendedMembers` (external identity), so
  // this works for both the pre-save shape (staged `contacts`) and the transient
  // post-save shape (populated `users`).
  #isSavingMatchingDraft(payload) {
    const payloadRoster = this.#extractNonCurrentUserRoster(payload);
    if (!payloadRoster.length) {
      return false;
    }

    const payloadIsCouple = Boolean(payload.data?.attributes?.is_couple);

    return this.store.peekAll(MODEL_NAMES.CONVERSATION).some(conv => {
      if (!conv.isNew || !conv.isSaving) {
        return false;
      }
      if (Boolean(conv.isCouple) !== payloadIsCouple) {
        return false;
      }
      const localRoster = conv.intendedMembers ?? [];
      if (localRoster.length !== payloadRoster.length) {
        return false;
      }
      return localRoster.every(local => payloadRoster.some(remote => matchingUsers(local, remote)));
    });
  }

  // Pulls user resources out of the JSON:API `included` array and returns them
  // as `{ externalId, externalScope }` records — the identity shape shared by
  // `messaging-contact` and `messaging-user` (see `matchingUsers`). System
  // users and the current user are filtered out so the roster lines up with a
  // virtual conversation's `intendedMembers`, which never includes either.
  #extractNonCurrentUserRoster(payload) {
    const included = payload.included ?? [];
    const currentUser = this.messaging.currentProfile?.user;
    const userSerializer = this.store.serializerFor(MODEL_NAMES.USER);

    const roster = [];
    for (const resource of included) {
      if (userSerializer.modelNameFromPayloadKey(resource.type) !== MODEL_NAMES.USER) continue;

      const externalId = resource.attributes?.external_id;
      const externalScope = resource.attributes?.external_scope;
      if (!externalId || !externalScope) continue;
      if (externalScope === EXTERNAL_SCOPE_TYPES.system) continue;
      if (currentUser && matchingUsers({ externalId, externalScope }, currentUser)) continue;

      roster.push({ externalId, externalScope });
    }
    return roster;
  }

  #handleReadActivity(payload) {
    const conversation = this.store.normalize(MODEL_NAMES.CONVERSATION, payload.data).data;
    this.messaging.currentProfile?.removeUnreadConversation(conversation.id);
  }

  #addOrReplaceConversationRecord(conversations, persistedConversation) {
    const persistedConversationUsers = filterSystemAndCurrentUser(
      persistedConversation.users,
      this.messaging.currentProfile?.user
    );
    const matchingUnsavedConversation = conversations.content.find(
      conv =>
        conv.isNew &&
        conversationHasExactContacts(conv, persistedConversationUsers, {
          isCouple: persistedConversation.isCouple,
        })
    );

    if (matchingUnsavedConversation) {
      persistedConversation.messageDraft = matchingUnsavedConversation.messageDraft;

      const index = conversations.content.indexOf(matchingUnsavedConversation);
      conversations.replace(index, 1, [persistedConversation]);
    } else {
      conversations.unshiftObjects([persistedConversation]);
    }
  }

  #conversationInList(conversations, conversationId) {
    return conversations.content.some(conv => conv.id === conversationId);
  }

  #ensureConversationInStore(conversationId, payload) {
    this.store.pushPayload(MODEL_NAMES.CONVERSATION, payload);
    return this.store.peekRecord(MODEL_NAMES.CONVERSATION, conversationId);
  }

  #payloadDisabled(payload) {
    return payload.data?.attributes?.disabled === true;
  }
}
